const heicExtension = /\.heic$/i
const maxWidth = 1920
const maxHeight = 1080
const jpegQuality = 0.8

export const isHeicFile = (file) => {
  if (!file) return false
  return heicExtension.test(file.name || '') || file.type === 'image/heic' || file.type === 'image/heif'
}

export const isSupportedImage = (file) => {
  if (!file) return false
  return (file.type && file.type.startsWith('image/')) || heicExtension.test(file.name || '')
}

const resizeAndCompressImage = async (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob)
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = objectUrl
    })

    const scale = Math.min(
      maxWidth / image.width,
      maxHeight / image.height,
      1
    )
    const targetWidth = Math.round(image.width * scale)
    const targetHeight = Math.round(image.height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas context unavailable')
    }
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

    const compressedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result)
          else reject(new Error('Failed to create image blob'))
        },
        'image/jpeg',
        jpegQuality
      )
    })

    const safeName = (filename || 'photo').replace(/\.[^/.]+$/, '.jpg')
    return new File([compressedBlob], safeName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const normalizeImageFile = async (file) => {
  if (!file) {
    throw new Error('No file provided')
  }

  let sourceBlob = file
  let outputName = file.name || 'photo.jpg'

  if (isHeicFile(file)) {
    const heic2any = await import('heic2any')
    sourceBlob = await heic2any.default({
      blob: file,
      toType: 'image/jpeg',
      quality: jpegQuality
    })
    outputName = outputName.replace(heicExtension, '.jpg')
  }

  return resizeAndCompressImage(sourceBlob, outputName)
}

export const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = (event) => resolve(event.target.result)
  reader.onerror = () => reject(new Error('Failed to read file'))
  reader.readAsDataURL(file)
})
